import { createSelector } from '@reduxjs/toolkit';

import getSensitiveDocumentTitleKeywords from '@app/libs/validation/getSensitiveDocumentTitleKeywords';
import { getRequestTasksState } from '@app/store/bulkRequest/requestTasks/selectors';
import RequestTask from '@app/store/bulkRequest/requestTasks/tasks/RequestTask';
import { RootState } from '@app/store/types';

export const tasksState = (state: RootState) => getRequestTasksState(state).tasks;

/**
 * 대량 전송 테스크의 총 수를 반환합니다.
 */
export const getTotalCount = (state: RootState) => tasksState(state).totalCount;

/**
 * 대량 전송 테스크를 조회할 때 한번에 조회하는 개수를 반환합니다.
 */
export const getCount = (state: RootState) => tasksState(state).count;

/**
 * 대량 전송 테스크를 조회 중인지 여부를 반환합니다.
 */
export const getIsFetching = (state: RootState) => tasksState(state).isFetching;

/**
 * 대량 전송 테스크를 조회 중인지 여부를 반환합니다.
 */
export const getIsUpdating = (state: RootState) => tasksState(state).isUpdating;

/**
 * 대량 전송 테스크를 조회한 현재 페이지를 반환합니다.
 */
export const getPage = (state: RootState) => tasksState(state).page;

/**
 * 대량 전송 테스크 데이터 객체(byId)를 반환합니다.
 */
export const getById = (state: RootState) => tasksState(state).byId;

/**
 * 대량 전송 테스크 리스트(배열)를 반환합니다.
 */
export const getTaskList = (state: RootState) => Object.values(getById(state)) as RequestTask[];

/**
 * 대량 전송 테스크 데이터 전체 id를 반환합니다.
 */
export const getAllIds = (state: RootState) => tasksState(state).allIds;

/**
 * 대량 전송 테스크 중 특정 id를 가진 테스크를 반환합니다.
 */
export const getTaskById = (state: RootState, props: { id: string }) => getById(state)[props.id];

export const getTaskNumber = (state: RootState, props: { id: string }) =>
  getAllIds(state).findIndex((id) => id === props.id) + 1;

/**
 * 대량 전송 테스크 중 특정 id를 가진 테스크의 Column values를 반환합니다.
 */
export const getColumnValues = (state: RootState, props: { id: string }) =>
  getTaskById(state, { id: props.id })?.columnValues;

/**
 * 대량 전송 테스크 중 특정 id를 가진 테스크의 문서id를 반환합니다.
 */
export const getDocumentId = (state: RootState, props: { id: string }) =>
  getTaskById(state, { id: props.id }).documentId;

/**
 * 대량 전송 테스크 중 특정 id를 가진 테스크의 status를 반환합니다.
 */
export const getStatus = (state: RootState, props: { id: string }) =>
  getTaskById(state, { id: props.id }).status;

/**
 * 대량 전송 테스크 중 특정 id를 가진 테스크의 failMessage를 반환합니다.
 */
export const getFailMessage = (state: RootState, props: { id: string }) =>
  getTaskById(state, { id: props.id }).failMessage;

export const getContactEmailListFromTasks = createSelector(getTaskList, (taskList): string[] => {
  return taskList.map((task) => task.columnValues[2]).filter((contact) => contact.includes('@'));
});

/**
 * 대량 전송 태스크 중 문서 제목에서 민감 키워드를 포함하고 있다면 포함된 민감 키워드를 반환합니다.
 */
export const getSensitiveDocumentTitleKeywordsFromTasks = createSelector(
  getTaskList,
  (taskList): string[] => {
    const DOCUMENT_TITLE = 0;
    return Array.from(
      new Set(
        taskList
          .map((task) => task.columnValues[DOCUMENT_TITLE])
          .map((title) => getSensitiveDocumentTitleKeywords(title))
          .flat()
      )
    );
  }
);
